import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { IPlatformPeopleAccountsDashboard } from '@brushfire/types'
import { RiBuildingLine, RiErrorWarningLine, RiGroupLine } from '@/shared/icons/remix'
import {
  GrowthConversionRateIcon,
  GrowthMobileSignupIcon,
  GrowthNewSensemakerSignupIcon,
  GrowthProfileCompletionIcon,
} from './PlatformGrowthIcons'
import {
  PLATFORM_DASHBOARD_TILE,
  PLATFORM_GROWTH_TILES,
  PLATFORM_POPULATION_TILES,
} from '../lib/platform-dashboard-layout'
import { PlatformMetricTile } from './PlatformMetricTile'
import { WeekMonthToggle } from './WeekMonthToggle'

interface Props {
  data: IPlatformPeopleAccountsDashboard
  growthPeriod: 'week' | 'month'
  onGrowthPeriodChange: (value: 'week' | 'month') => void
  isPeriodRefreshing?: boolean
}

const POPULATION_ICONS = {
  activeSensemakers: <RiBuildingLine size={24} />,
  totalSignalProviders: <RiGroupLine size={24} />,
  verifiedEkyc: <RiGroupLine size={24} />,
  failedEkyc: <RiErrorWarningLine size={24} />,
} as const

const GROWTH_ICONS = {
  mobileSignups: <GrowthMobileSignupIcon />,
  newSensemakerSignups: <GrowthNewSensemakerSignupIcon />,
  sensemakerConversionRate: <GrowthConversionRateIcon />,
  profileCompletionRate: <GrowthProfileCompletionIcon />,
} as const

function growthTileValue(
  tile: (typeof PLATFORM_GROWTH_TILES)[number],
  data: IPlatformPeopleAccountsDashboard,
): number | null {
  if (tile.format === 'percent') {
    return tile.key === 'sensemakerConversionRate'
      ? data.growth.sensemakerConversionRate
      : data.growth.profileCompletionRate
  }
  return tile.key === 'mobileSignups'
    ? data.growth.mobileSignups
    : data.growth.newSensemakerSignups
}

const sectionTitleSx = {
  fontSize: PLATFORM_DASHBOARD_TILE.sectionTitleFontSize,
  fontWeight: 600,
  color: PLATFORM_DASHBOARD_TILE.sectionTitleColor,
  mb: 2,
  letterSpacing: '-0.44px',
} as const

export function PlatformPopulationTiles({ data }: Pick<Props, 'data'>) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={sectionTitleSx}>Platform Population</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        {PLATFORM_POPULATION_TILES.map((tile) => {
          const isNa = 'na' in tile && tile.na
          const value = isNa
            ? 'N/A'
            : tile.key === 'activeSensemakers'
              ? data.population.activeSensemakers
              : data.population.totalSignalProviders
          return (
            <PlatformMetricTile
              key={tile.key}
              testId={`platform-population-tile-${tile.key}`}
              label={tile.label}
              value={value}
              valueFormat="count"
              bg={tile.bg}
              iconBg={tile.iconBg}
              labelColor={tile.labelColor}
              icon={POPULATION_ICONS[tile.key]}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export function PlatformGrowthTiles({
  data,
  growthPeriod,
  onGrowthPeriodChange,
  isPeriodRefreshing = false,
}: Props) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={sectionTitleSx}>Growth & Conversion</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 3,
        }}
      >
        {PLATFORM_GROWTH_TILES.map((tile) => {
          const headerActions =
            'toggle' in tile && tile.toggle ? (
              <WeekMonthToggle
                value={growthPeriod}
                onChange={onGrowthPeriodChange}
                testId={`platform-growth-toggle-${tile.key}`}
                variant="metric"
                accentColor={tile.accentColor}
                trackBg={tile.toggleTrackBg}
              />
            ) : undefined

          const displayValue = growthTileValue(tile, data)

          const periodToggleTile = 'toggle' in tile && tile.toggle

          return (
            <Box
              key={tile.key}
              sx={{
                opacity: isPeriodRefreshing && periodToggleTile ? 0.45 : 1,
                transition: 'opacity 0.2s ease',
              }}
              aria-busy={isPeriodRefreshing && periodToggleTile ? true : undefined}
            >
              <PlatformMetricTile
                testId={`platform-growth-tile-${tile.key}`}
                label={tile.label}
                value={displayValue}
                valueFormat={tile.format}
                bg={tile.bg}
                iconBg={tile.iconBg}
                labelColor={tile.labelColor}
                icon={GROWTH_ICONS[tile.key]}
                headerActions={headerActions}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
